import cn from 'classnames';
import React, { PropsWithChildren, useState } from 'react';
import { BasicCheckbox, BasicRadio } from '../basic-inputs';
import { ElementOrArrayOfElement } from '../common/Types';
import { ColumnData } from './types';

export interface RowsProps {
  children?: ElementOrArrayOfElement<RowProps>;
}

export const DatagridRows: React.FC<RowsProps> = props => <>{props.children}</>;

export type RowProps = PropsWithChildren<{
  className?: string;
  rowKey?: string;
  columnData?: ColumnData[];
  expandable?: boolean;
  selection?: {
    checked: boolean;
    type: 'radio' | 'checkbox';
    selectionChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  };
  detailPane?: {
    open: boolean;
    detailHandler: (e: any) => void;
    rowKey: string | undefined;
  };
  disableSelection?: boolean;
}>;

export const DatagridRow = React.forwardRef<HTMLDivElement, RowProps>(
  (props, ref) => {
    const {
      children,
      columnData,
      expandable,
      selection,
      className,
      detailPane,
      rowKey,
      disableSelection
    } = props;
    const classNames = cn(
      'datagrid-row',
      {
        'datagrid-selected': selection && selection.checked
      },
      className
    );
    const [expanded, setExpanded] = useState(false);
    const toggleExpand = () => setExpanded(prevExpanded => !prevExpanded);

    const renderDatagridRowDetails = () => {
      return React.Children.map(children, child => {
        if (React.isValidElement(child) && child.type === DatagridRowDetail) {
          return child;
        } else {
          return null;
        }
      });
    };

    const renderDatagridCells = () => {
      return React.Children.map(children, (child, index) => {
        if ((detailPane?.open && index < 1) || !detailPane?.open) {
          if (
            React.isValidElement<CellProps>(child) &&
            child.type === DatagridCell
          ) {
            const { width, fixed } = columnData![index];
            return React.cloneElement(child, {
              fixed,
              width
            });
          } else {
            return null;
          }
        }
      });
    };

    return (
      <div ref={ref} className={classNames} role="rowgroup">
        <div
          className={cn('datagrid-row-master datagrid-row-flex', {
            'datagrid-row-detail-open':
              detailPane && detailPane.open && rowKey === detailPane.rowKey
          })}
          role="row">
          <div className="datagrid-row-sticky">
            {selection && (
              <DatagridCell
                fixed={true}
                select={true}
                disableSelection={disableSelection}>
                {selection.type === 'radio' && (
                  <BasicRadio
                    attrs={{ 'aria-label': 'Select Row' }}
                    checked={selection.checked}
                    onChange={selection.selectionChange}
                    disabled={disableSelection}
                  />
                )}
                {selection.type === 'checkbox' && (
                  <BasicCheckbox
                    attrs={{ 'aria-label': 'Select Row' }}
                    checked={selection.checked}
                    onChange={selection.selectionChange}
                    disabled={disableSelection}
                  />
                )}
              </DatagridCell>
            )}
            {detailPane && (
              <DatagridCell fixed={true} detailPane={true}>
                <button
                  className={cn('datagrid-detail-caret-button', {
                    'is-open': detailPane.open && rowKey === detailPane.rowKey
                  })}
                  type="button"
                  onClick={detailPane.detailHandler}>
                  <clr-icon
                    class="datagrid-detail-caret-icon"
                    shape="angle-double"
                    dir={
                      detailPane.open && rowKey === detailPane.rowKey
                        ? 'left'
                        : 'right'
                    }
                    title={
                      detailPane.open && rowKey === detailPane.rowKey
                        ? 'Close'
                        : 'Open'
                    }
                    size="16"
                  />
                </button>
              </DatagridCell>
            )}
            {expandable && (
              <DatagridCell fixed={true} expandable={true}>
                <button
                  className="datagrid-expandable-caret-button"
                  type="button"
                  onClick={toggleExpand}
                  aria-label={expanded ? 'Open' : 'Close'}
                  aria-expanded={expanded}>
                  <clr-icon
                    className="datagrid-expandable-caret-icon"
                    shape={`caret ${expanded ? 'down' : 'right'}`}
                    title={expanded ? 'Collapse' : 'Expand'}
                  />
                </button>
              </DatagridCell>
            )}
          </div>
          <div className="datagrid-row-scrollable">
            <div className="datagrid-scrolling-cells">
              {renderDatagridCells()}
            </div>
            {expanded && renderDatagridRowDetails()}
          </div>
        </div>
      </div>
    );
  }
);

type CellProps = PropsWithChildren<{
  width?: string;
  fixed?: boolean;
  expandable?: boolean;
  select?: boolean;
  detailPane?: boolean;
  disableSelection?: boolean;
}>;

export const DatagridCell = React.forwardRef<HTMLDivElement, CellProps>(
  (props, ref) => {
    const {
      width,
      fixed,
      expandable,
      select,
      detailPane,
      children,
      disableSelection
    } = props;
    const classNames = cn('datagrid-cell', {
      'datagrid-fixed-column': fixed,
      'datagrid-select': select,
      'datagrid-expandable-caret': expandable,
      'datagrid-detail-caret': detailPane,
      'clr-form-control-disabled': disableSelection
    });

    return (
      <div ref={ref} className={classNames} style={{ width }} role="gridcell">
        {children}
      </div>
    );
  }
);

export const DatagridRowDetail = React.forwardRef<
  HTMLDivElement,
  PropsWithChildren<{}>
>((props, ref) => {
  return (
    <div
      ref={ref}
      className="datagrid-container datagrid-row-flex datagrid-row-detail">
      {props.children}
    </div>
  );
});
